import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { RemmiConfirmService } from '@common/remmi-confirm/remmi-confirm.service';
import { ExternalEoiService } from '@services/api/external-eoi.service';
import { shouldHideError } from '@services/helpers/server-error-silence';
import { Utils } from '@services/helpers/utils';
import { ToasterService } from '@services/util/toaster.service';
import { commonImages } from '@images/common-images';
import {
  EXTERNAL_EOI_STATUS_META,
  ExternalEoiLink,
  buildExternalEoiShareUrl,
} from '@modules/my-projects/edit-project/project-edit/external-eoi/external-eoi.model';

/**
 * ExternalEoiLinksComponent
 *
 * Shows the external EOI links belonging to one record, so an agent can see,
 * copy, reopen or reissue the link from the record itself rather than only
 * from the project's External EOI tab.
 *
 * Scope it with exactly one of `eoiId` / `leadId`. Nothing renders without one:
 * a project's other links must never appear on an individual record.
 */
@Component({
  selector: 'app-external-eoi-links',
  templateUrl: './external-eoi-links.component.html',
  styleUrls: ['./external-eoi-links.component.scss'],
})
export class ExternalEoiLinksComponent implements OnChanges {
  @Input() projectId: string | null = null;

  /** Show only links generated against this EOI. */
  @Input() eoiId: string | null = null;

  /** Show only links generated from this lead. */
  @Input() leadId: string | null = null;

  /** Hide regenerate/deactivate when the host says the user may not edit. */
  @Input() canManage = true;

  @Input() heading = 'External EOI Link';

  /**
   * The agent responsible for this lead/EOI, shown in place of `link.generated_by_name` —
   * reported live: the panel showed whoever happened to click "Generate" (an admin, an assistant,
   * ops), which is not necessarily the agent actually working the buyer. Optional so a host that
   * hasn't been updated yet still falls back to the generator's name rather than showing nothing.
   */
  @Input() agentName = '';

  links: ExternalEoiLink[] = [];
  loading = false;
  /**
   * True when the last fetch failed. Shown as its own state rather than folded into an empty
   * `links` array — an agent who just generated a link and then sees nothing here has no way to
   * tell "the request failed" from "there genuinely is no link", and reported the first as
   * "I created an EOI for this person and now it's not there".
   */
  loadError = false;
  readonly statusMeta = EXTERNAL_EOI_STATUS_META;
  /** The app's one EOI glyph (also used on the price list's own EOI button), rather than a new one for this button. */
  readonly eoiIcon = commonImages.eoi_icon;

  /** The record the links on screen were loaded for, so a host rebinding its inputs is not mistaken for a new record. */
  private loadedScopeId: string | null = null;

  constructor(
    private externalEoi: ExternalEoiService,
    private toaster: ToasterService,
    private router: Router,
    private remmiConfirm: RemmiConfirmService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId'] || changes['eoiId'] || changes['leadId']) {
      this.reload();
    }
  }

  /**
   * Fetch and filter this record's links.
   *
   * The list endpoint is project-scoped. The record id is sent so a backend
   * that supports the filter can narrow the query, and the response is
   * filtered here regardless — an unfiltered backend must not leak a
   * project's other links onto this record.
   *
   * Public so a host can call it after generating a link.
   */
  reload(): void {
    const scopeId = this.eoiId || this.leadId;

    // Only a genuine change of record empties the panel. A host can null these
    // inputs out transiently — a lead switching into edit mode rebinds its
    // detail object — and blanking on that made an existing link look as though
    // it had been lost, which is exactly what an agent reports as "the link
    // disappeared when I clicked edit".
    if (scopeId && scopeId !== this.loadedScopeId) {
      this.links = [];
      this.loadedScopeId = null;
    }
    if (!this.projectId || !scopeId) return;

    this.loadedScopeId = scopeId;
    this.loading = true;
    this.loadError = false;
    this.externalEoi.getLinks(this.projectId, { eoiId: this.eoiId, leadId: this.leadId }).subscribe({
      next: (res: any) => {
        const all: ExternalEoiLink[] = res?.data || [];
        // Newest first, so the link currently in play (or just generated) leads the list.
        this.links = all.filter(l => this.belongsToRecord(l)).sort((a, b) => this.linkTime(b) - this.linkTime(a));
        this.loading = false;
      },
      error: () => {
        // Not cleared to `[]`: a transient failure (the API mid-deploy, a dropped connection)
        // must not read as "no link was ever generated" when one plainly was moments ago.
        this.loadError = !shouldHideError(null);
        this.loading = false;
      },
    });
  }

  private belongsToRecord(link: ExternalEoiLink): boolean {
    if (this.eoiId) return String(link?.eoi_id || '') === String(this.eoiId);
    if (this.leadId) return String(link?.lead_id || '') === String(this.leadId);
    return false;
  }

  private linkTime(link: ExternalEoiLink): number {
    const t = new Date(link?.generated_at || link?.created_at || 0).getTime();
    return isNaN(t) ? 0 : t;
  }

  /**
   * The one link ever shown here — the record's most recent. A lead/EOI can accumulate several
   * links over time (one per "Send External EOI Form"), and listing every one of them read as
   * clutter with no way to tell which was live; earlier links belong in the record's History, not
   * as a row (or an expandable list) sitting here.
   */
  get currentLink(): ExternalEoiLink | null {
    return this.links[0] || null;
  }

  /** Always rebuilt against the current origin — see buildExternalEoiPublicUrl. */
  url(link: ExternalEoiLink): string | null {
    return buildExternalEoiShareUrl(link, window.location.origin);
  }

  copy(link: ExternalEoiLink): void {
    const url = this.url(link);
    if (!url) {
      this.toaster.Warning('This link has no URL yet.');
      return;
    }
    Utils.copyToClipboard(url, (ok: boolean) =>
      ok ? this.toaster.Success('Link copied to clipboard.') : this.toaster.Error('Could not copy the link.')
    );
  }

  /** Payload for the inline `<app-external-eoi-form>` view, once loaded. */
  viewConfig: any = null;
  viewLoading = false;
  /** The link behind the open "View Link" overlay — its id is what the agent's "Save for buyer" draft is saved against. */
  viewingLink: ExternalEoiLink | null = null;

  /**
   * Open the buyer's form inline via the authenticated view endpoint.
   *
   * The public URL used to be opened directly, which sent the agent through the
   * buyer's own OTP gate — a code that goes to the buyer's inbox, not the
   * agent's. The authenticated endpoint returns the same payload with no
   * passcode step, rendered through the same `<app-external-eoi-form>`
   * component the Preview screen already uses.
   */
  open(link: ExternalEoiLink): void {
    if (!link?._id) {
      this.toaster.Warning('This link has no URL yet.');
      return;
    }
    this.viewLoading = true;
    this.viewConfig = null;
    this.viewingLink = link;
    this.externalEoi.getLinkView(link._id).subscribe({
      next: (res: any) => {
        this.viewLoading = false;
        this.viewConfig = res?.data || null;
        if (!this.viewConfig) this.toaster.Error('Could not load this link.');
      },
      error: (err: any) => {
        this.viewLoading = false;
        this.viewingLink = null;
        this.toaster.Error(err?.status === 403 ? 'You are not authorised to view this link.' : 'Could not load this link.');
      },
    });
  }

  closeView(): void {
    this.viewConfig = null;
    this.viewLoading = false;
    this.viewingLink = null;
  }

  /**
   * True once the buyer has completed the form, so the panel can point at what
   * their answers produced rather than only at the link they were sent.
   */
  isSubmitted(link: ExternalEoiLink): boolean {
    return link?.status === 'submitted' || link?.status === 'reviewed' || !!link?.submitted_at;
  }

  /**
   * Whether to offer a way through to the EOI this link produced.
   *
   * Suppressed when the panel is already sitting on that EOI — the button would
   * only lead back to the page it is on.
   */
  canOpenEoi(link: ExternalEoiLink): boolean {
    return !!link?.eoi_id && !this.eoiId;
  }

  /**
   * Open the EOI record the submitted form created or updated.
   *
   * Navigates into the project's own EOI tab (`/projects/edit_/:projectId/eoi`, the same place
   * `EoiComponent.loadItem` opens a row from) rather than `/listings/listing-eoi` — a different
   * listing-side page agents land on with the record locked behind extra clicks, not where "View
   * EOI" is supposed to take them.
   */
  openEoi(link: ExternalEoiLink): void {
    if (!link?.eoi_id || !link?.project_id) return;
    this.router.navigate(['/projects/edit_', link.project_id, 'eoi'], { queryParams: { id: link.eoi_id } });
  }

  deactivate(link: ExternalEoiLink): void {
    if (!link?._id) return;
    this.externalEoi.deactivateLink(link._id).subscribe({
      next: () => {
        this.toaster.Success('Link deactivated.');
        this.reload();
      },
      error: () => this.toaster.Error('Could not deactivate link.'),
    });
  }

  /**
   * Permanently removes a deactivated link so it does not have to be kept around once it can no
   * longer be used. Only offered once a link is already `cancelled` — deactivate first, delete
   * second — same as the Generated EOIs table on the project's External EOI tab.
   */
  delete(link: ExternalEoiLink): void {
    if (!link?._id) return;
    this.remmiConfirm
      .delete({ title: 'Delete Link?', message: 'This link will be permanently deleted.', confirmLabel: 'Delete Permanently' })
      .subscribe(isConfirmed => {
        if (!isConfirmed) {
          return;
        }
        this.externalEoi.deleteLink(link._id).subscribe({
          next: () => {
            this.toaster.Success('Link deleted.');
            this.reload();
          },
          error: () => this.toaster.Error('Could not delete link.'),
        });
      });
  }

  regenerate(link: ExternalEoiLink): void {
    if (!link?._id) return;
    this.externalEoi.regenerateLink(link._id).subscribe({
      next: (res: any) => {
        const url = this.url(res?.data);
        if (url) Utils.copyToClipboard(url, () => {});
        this.toaster.Success('A new link has been generated and copied to clipboard.');
        this.reload();
      },
      error: () => this.toaster.Error('Could not regenerate link.'),
    });
  }
}
